import { debounce } from '@zg-rentals/zdash';
import type { ComboboxOptionType } from '@zillow/constellation-10';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery } from '@zg-rentals/react-query5';
import { SatelliteWebLandlordPropertyHandler_search } from '@zg-rentals/ts-satellite-client';
import type {
  SatelliteWebLandlordPropertySearchRequestDto,
  SatelliteWebLandlordPropertySearchResponseDto,
} from '@zg-rentals/ts-satellite-client';
import { logError } from '@zg-rentals/log-error';
import { usePropertyFilterHook_GetPropertyAddressLazyQuery } from '../types/GraphqlTypes';
import { InboxPageContext } from '../contexts/InboxPageContextProvider';
import { logApolloError } from '../utils/errorUtils';

export type PropertyListingItem = {
  label: string;
  value: string;
  meta?: string;
};

export type UpdatePathWithListingAlias = (listingAlias: string) => void;

type UsePropertyFiltersArgs = {
  defaultListingAliasOnLoad?: string;
  resetPropertyFilter?: () => void;
  updatePathWithListingAlias: UpdatePathWithListingAlias;
};

// Filter special characters in property search input, but include characters such as - /
// since they can be found in the property name
const filterSpecialCharacters = (str: string) => str.trim().replace(/[^a-zA-Z0-9-/ ]/g, '');

const formatPropertyListings = (
  listings: SatelliteWebLandlordPropertySearchResponseDto['listings'],
): Array<PropertyListingItem> => {
  return listings.map((listing) => {
    const { address, isCommunityTypeListing, listingAlias, name } = listing;
    const { city, state, street, unit, zip } = address;
    const cityStateZip = `${city.trim()}, ${state} ${zip}`;
    const namePrefix = isCommunityTypeListing && name ? name.trim() : '';
    const unitWithCityStateZip = unit ? `#${unit} ${cityStateZip}` : cityStateZip;
    const streetLabel = `${street.trim()} ${unitWithCityStateZip}`;

    return {
      value: listingAlias || streetLabel,
      label: namePrefix || streetLabel,
      meta: namePrefix ? streetLabel : '',
    };
  });
};

const ERROR_FETCHING_PROPERTY_LISTINGS =
  'Sorry, we are having trouble getting property listings to search by. Please refresh the page and try again.';
const ERROR_FETCHING_PROPERTY_ADDRESS = 'Sorry, we were unable to filter by that property. Please try a new search.';

/**
 * @param defaultListingAliasOnLoad listingAlias to set on page load
 * @param resetPropertyFilter Function to remove selected listingAlias in the URL if user input is empty or API call errors
 * @param updatePathWithListingAlias Function to update the URL with selected listingAlias
 * @description Helper functions to handle filtering a list of properties based on user input or selection
 * and updating the URL with selected property listingAlias
 */
export default ({
  defaultListingAliasOnLoad,
  resetPropertyFilter,
  updatePathWithListingAlias,
}: UsePropertyFiltersArgs) => {
  const { listingAlias } = useContext(InboxPageContext);

  const [searchText, setSearchText] = useState('');
  const [defaultListingAddress, setDefaultListingAddress] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const {
    data: propertyListingData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isPropertyListingsDataLoading,
    error: propertyListingsError,
  } = useInfiniteQuery({
    queryKey: ['propertySearch', debouncedSearchText],
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
      const requestData: SatelliteWebLandlordPropertySearchRequestDto = {
        startKeyExclusive: pageParam,
      };

      // Only include searchText if it's not empty
      if (debouncedSearchText) {
        requestData.searchText = debouncedSearchText;
      }

      return SatelliteWebLandlordPropertyHandler_search(requestData);
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pager.hasMore ? lastPage.pager.endKey : undefined;
    },
    initialPageParam: undefined as string | undefined,
    placeholderData: (previousData) => previousData, // Keep previous data while loading new query
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    refetchOnWindowFocus: true, // Refetch when user comes back to tab (if stale)
    retry: 2, // Retry failed requests twice
  });

  // Handle errors
  useEffect(() => {
    if (propertyListingsError) {
      setErrorMessage(ERROR_FETCHING_PROPERTY_LISTINGS);
      logError({
        error: propertyListingsError,
        context: {
          details: 'PropertyFilterHook - Error fetching property listings',
          listingAlias,
          searchText,
          debouncedSearchText,
        },
      });
    }
  }, [propertyListingsError, listingAlias, searchText, debouncedSearchText]);

  // Flatten all pages and format listings
  const propertyListings = useMemo(() => {
    if (!propertyListingData?.pages) return [];

    const allListings = propertyListingData.pages.flatMap((page) => page.listings);
    return formatPropertyListings(allListings);
  }, [propertyListingData]);

  const hasMoreToPaginate = hasNextPage ?? false;

  const [getPropertyAddressForListingAlias, { data: propertyAddressData, loading: isPropertyAddressLoading }] =
    usePropertyFilterHook_GetPropertyAddressLazyQuery({
      onError(err) {
        if (resetPropertyFilter) {
          resetPropertyFilter();
        }

        setErrorMessage(ERROR_FETCHING_PROPERTY_ADDRESS);
        logApolloError(err, {
          details: 'PropertyFilterHook - Error fetching property address for listing',
          listingAlias,
        });
      },
    });

  // Look up property address based on listingAlias from the url
  // and display it in the property address input
  const { street = '', unit = '' } = propertyAddressData?.ZRM_getPropertyAddress || {};
  const listingAddress = unit ? `${street} #${unit}` : street;
  const hasCheckedForPropertyLabelOnLoad = useRef(false);

  useEffect(() => {
    if (listingAddress) {
      setDefaultListingAddress(listingAddress);
    }
  }, [listingAddress]);

  // On initial load, if the url has a property filter, check if the filtered property is in the paginated options.
  // If not found, fetch its address so Combobox can display the label instead of the raw value (listingAlias).
  useEffect(() => {
    if (hasCheckedForPropertyLabelOnLoad.current || propertyListings.length === 0) return;
    const needsFetch =
      defaultListingAliasOnLoad && !propertyListings.some((listing) => listing.value === defaultListingAliasOnLoad);

    if (defaultListingAliasOnLoad && needsFetch) {
      getPropertyAddressForListingAlias({
        variables: {
          listingAlias: defaultListingAliasOnLoad,
        },
        returnPartialData: true,
      });
    }

    hasCheckedForPropertyLabelOnLoad.current = true;
  }, [propertyListings.length > 0, defaultListingAliasOnLoad, getPropertyAddressForListingAlias]); // Once on initial options load

  const debouncedSearch = useRef(
    debounce((inputSearchText: string) => {
      const filteredSearchText = filterSpecialCharacters(inputSearchText);
      setDebouncedSearchText(filteredSearchText);
    }, 500),
  ).current;

  useEffect(() => {
    // When the component unmounts, the search request could
    // still be in progress, so we cancel the debounced function
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSelectProperty = (selectedPropertyListingItem: ComboboxOptionType) => {
    setDefaultListingAddress('');

    if (typeof selectedPropertyListingItem === 'object') {
      updatePathWithListingAlias(selectedPropertyListingItem.value);
    }
  };

  const handleChange = (inputSearchText = '', event: React.SyntheticEvent, reason: string) => {
    // We only run the search to filter properties as the user types,
    // and not when a property is selected
    if ((reason !== 'clear' && event.type === 'click') || ('key' in event && event.key === 'Enter')) {
      return;
    }

    setSearchText(inputSearchText);

    if (errorMessage) {
      setErrorMessage('');
    }

    if (!inputSearchText) {
      setDefaultListingAddress(''); // Clear the default listing address
      if (resetPropertyFilter) {
        resetPropertyFilter();
      }
    }

    debouncedSearch(inputSearchText);
  };

  const handleScroll = async (isLastPropertyVisible: boolean) => {
    if (isLastPropertyVisible && hasMoreToPaginate && !isFetchingNextPage) {
      try {
        await fetchNextPage();
      } catch (error) {
        logError({ error, context: 'PropertyFilterHook - Error fetching more property listings' });
      }
    }
  };

  return {
    defaultListingAddress,
    errorMessage,
    handleChange,
    handleScroll,
    handleSelectProperty,
    hasMoreToPaginate,
    isLoadingSearchResults: isPropertyListingsDataLoading || isFetchingNextPage,
    propertyListings,
    searchText,
    shouldShowSkeleton:
      defaultListingAliasOnLoad &&
      (isPropertyListingsDataLoading || isPropertyAddressLoading) &&
      hasCheckedForPropertyLabelOnLoad,
  };
};
